import { User, UserRole, Permission } from '@/types';

export class PermissionManager {
  /**
   * Check if a user has a specific permission
   */
  static hasPermission(user: User | null, permission: Permission): boolean {
    if (!user) return false;
    
    // Admins have all permissions
    if (user.role === UserRole.ADMIN) return true;
    
    return user.permissions.includes(permission);
  }

  /**
   * Check if a user has any of the given permissions
   */
  static hasAnyPermission(user: User | null, permissions: Permission[]): boolean {
    if (!user) return false;
    if (user.role === UserRole.ADMIN) return true;
    
    return permissions.some(p => user.permissions.includes(p));
  }

  /**
   * Check if a user has all of the given permissions
   */
  static hasAllPermissions(user: User | null, permissions: Permission[]): boolean {
    if (!user) return false;
    if (user.role === UserRole.ADMIN) return true;
    
    return permissions.every(p => user.permissions.includes(p));
  }

  /**
   * Check if a user has a specific role
   */
  static hasRole(user: User | null, role: UserRole | UserRole[]): boolean {
    if (!user) return false;
    
    if (Array.isArray(role)) {
      return role.includes(user.role);
    }
    
    return user.role === role;
  }

  /**
   * Check if a user can perform an action on a resource
   * This is for resource-level permissions (e.g., owner of a property)
   */
  static canPerformAction(
    user: User | null, 
    action: Permission, 
    resourceOwnerId?: string
  ): boolean {
    if (!user) return false;
    
    // Admin bypass
    if (user.role === UserRole.ADMIN) return true;
    
    // Basic permission check
    if (!this.hasPermission(user, action)) return false;
    
    // If it's a write/delete action and there's a resource owner, check ownership
    const isWriteAction = [
      Permission.WRITE_PROPERTIES, 
      Permission.DELETE_PROPERTIES, 
      Permission.WRITE_CLIENTS, 
      Permission.DELETE_CLIENTS
    ].includes(action);
    
    if (isWriteAction && resourceOwnerId && user.role === UserRole.AGENT) {
      return user.id === resourceOwnerId;
    }
    
    return true;
  }
}
